import { forwardRef, MutableRefObject, Ref, useCallback, useEffect, useRef, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Chip } from '@mui/material';

import { DisplayDateFormat, formatServerDate } from '@/utils/formatDate';

import DateQueryForm from '../FilterPopper/DateQueryForm';
import FilterPopper from '../FilterPopper/FilterPopper';
import Filter, { DateFilter } from '../models/Filter';

interface DateFilterChipProps {
  filter: DateFilter;
  onClick: (filter: Filter) => void;
  onDelete: (filter: Filter) => void;
}

interface DateFilterChipPopoverProps {
  popoverOpen: boolean;
  filter: DateFilter;
  onClosePopover: (oldFilter: Filter, newFilter?: Filter) => void;
}

interface DateFilterChipWithPopoverProps {
  filter: DateFilter;
  onClick?: (filter: Filter) => void;
  onDelete?: (filter: Filter) => void;
  onClosePopover?: (oldFilter: Filter, newFilter?: Filter) => void;
  'data-testid'?: string;
}

function formatPropertyValue(value: string) {
  // While this value is picked in local time, it's truncated to YYYY-MM-DD which is parsed as ISO,
  // effectively making it server time. The S&F code is pretty sloppy about this, with some table
  // backend APIs accepting the truncated date string, and others (e.g. Dual Control) accepting a
  // timezone-adjusted ISO 8601 string.
  return formatServerDate(new Date(value), DisplayDateFormat.ShortDate);
}

const DateFilterChip = forwardRef(
  ({ filter, onClick, onDelete }: DateFilterChipProps, ref: Ref<HTMLDivElement>) => {
    const handleClick = useCallback(() => onClick?.(filter), [onClick, filter]);
    const handleDelete = useCallback(() => onDelete?.(filter), [onDelete, filter]);
    const color = filter.hasError ? 'error' : undefined;
    const icon = filter.hasError ? <ErrorOutlineIcon data-testid="error-icon" /> : undefined;
    const label =
      filter.from && filter.to
        ? `${filter.property.label} ${formatPropertyValue(filter.from)} - ${formatPropertyValue(filter.to)}`
        : filter.property.label;

    return (
      <Chip
        ref={ref}
        label={label}
        color={color}
        icon={icon}
        onClick={handleClick}
        onDelete={handleDelete}
        sx={{ maxWidth: 340 }}
        data-testid={filter.property.label}
      />
    );
  }
);
DateFilterChip.displayName = 'DateFilterChip';

const DateFilterPopover = forwardRef(
  (
    { popoverOpen, filter, onClosePopover }: DateFilterChipPopoverProps,
    chipRef: MutableRefObject<Element>
  ) => {
    const [from, setFrom] = useState<string>(filter.from);
    const [to, setTo] = useState<string>(filter.to);

    const handleClosePopover = useCallback(
      (applyChanges: boolean) => {
        if (applyChanges) {
          onClosePopover?.(filter, { ...filter, from, to });
        } else {
          onClosePopover?.(filter, { ...filter, hasError: true });
        }
      },
      [onClosePopover, filter, from, to]
    );

    return (
      chipRef.current && (
        <FilterPopper
          anchorEl={chipRef.current}
          open={popoverOpen}
          title={filter.property.label}
          onClose={handleClosePopover}
        >
          <DateQueryForm from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
        </FilterPopper>
      )
    );
  }
);
DateFilterPopover.displayName = 'DateFilterPopover';

const DateFilterChipWithPopover = ({
  filter,
  onClick,
  onDelete,
  onClosePopover,
  'data-testid': dataTestId,
}: DateFilterChipWithPopoverProps) => {
  const chipRef = useRef(null);

  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (chipRef.current) {
      setPopoverOpen(filter.open);
    }
  }, [chipRef, setPopoverOpen, filter]);

  return (
    <Box data-testid={dataTestId}>
      <DateFilterChip ref={chipRef} filter={filter} onClick={onClick} onDelete={onDelete} />
      <DateFilterPopover
        ref={chipRef}
        filter={filter}
        popoverOpen={popoverOpen}
        onClosePopover={onClosePopover}
      />
    </Box>
  );
};

export { DateFilterChipWithPopoverProps as DateFilterChipProps };
export default DateFilterChipWithPopover;
